import { useCallback, useId, useRef, useState } from 'react';

// Shared photo/file upload foundation, per the v6 architecture doc §5.2:
// - "Take Photo" and "Choose from Library/Files" are two distinct actions.
// - Multiple selection is allowed.
// - Selected files show a preview thumbnail before upload.
// - Each file has its own independent upload progress.
// - Each file resolves to success (uploaded thumbnail) or a retry/error
//   state — a failed upload never silently disappears or blocks the others.
//
// `uploadFile` is pluggable so this component is fully testable today and
// can be wired to a real Drive-backed endpoint in a later stage without any
// change to the component itself or its call sites.

export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface UploadItem {
  id: string;
  file: File;
  previewUrl: string;
  status: UploadStatus;
  progress: number;
  resultUrl?: string;
  errorMessage?: string;
}

export interface UploadFieldProps {
  label: string;
  multiple?: boolean;
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<{ url: string }>;
  onUploaded?: (item: UploadItem) => void;
}

function makeId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function UploadField({ label, multiple = true, uploadFile, onUploaded }: UploadFieldProps) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const startUpload = useCallback(
    (item: UploadItem) => {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: 'uploading', progress: 0, errorMessage: undefined } : i)));
      uploadFile(item.file, (percent) => {
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, progress: percent } : i)));
      })
        .then(({ url }) => {
          setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: 'success', progress: 100, resultUrl: url } : i)));
          onUploaded?.({ ...item, status: 'success', progress: 100, resultUrl: url });
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Upload failed.';
          setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: 'error', errorMessage: message } : i)));
        });
    },
    [uploadFile, onUploaded],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const newItems: UploadItem[] = Array.from(fileList).map((file) => ({
        id: makeId(),
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'pending',
        progress: 0,
      }));
      setItems((prev) => [...prev, ...newItems]);
      newItems.forEach(startUpload);
    },
    [startUpload],
  );

  const retry = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (item) startUpload(item);
    },
    [items, startUpload],
  );

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-slate-200">{label}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="rounded-lg border border-ink-border bg-ink-raised px-3 py-2 text-sm font-semibold text-slate-200 hover:border-gold-700 hover:text-gold-200"
        >
          Take Photo
        </button>
        <button
          type="button"
          onClick={() => libraryInputRef.current?.click()}
          className="rounded-lg border border-ink-border bg-ink-raised px-3 py-2 text-sm font-semibold text-slate-200 hover:border-gold-700 hover:text-gold-200"
        >
          Choose from Library
        </button>
      </div>
      <input
        ref={cameraInputRef}
        id={`${inputId}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={libraryInputRef}
        id={`${inputId}-library`}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {items.length > 0 && (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {items.map((item) => (
            <li key={item.id} className="relative overflow-hidden rounded-lg border border-ink-border">
              <img src={item.previewUrl} alt="" className="h-24 w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-black/70 px-1.5 py-1 text-center text-[11px] text-white">
                {item.status === 'uploading' && <span>Uploading… {item.progress}%</span>}
                {item.status === 'success' && <span>Uploaded</span>}
                {item.status === 'pending' && <span>Waiting…</span>}
                {item.status === 'error' && (
                  <button type="button" onClick={() => retry(item.id)} className="text-gold-200 underline">
                    Retry
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
